// SOFA / qSOFA — Sepsis screening & organ dysfunction scoring.
// Sources: Sepsis-3 (JAMA 2016); Vincent et al. Intensive Care Med 1996.

import React, { useState, useMemo } from "react";
import { AlertTriangle, Activity, ShieldAlert } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { cn } from "@/lib/utils";

const QSOFA_ITEMS = (isID) => [
  {
    key: "mental",
    label: isID ? "Perubahan status mental (GCS < 15)" : "Altered mentation (GCS < 15)",
  },
  {
    key: "rr",
    label: isID ? "Laju napas ≥ 22 /menit" : "Respiratory rate ≥ 22 /min",
  },
  {
    key: "sbp",
    label: isID ? "Tekanan darah sistolik ≤ 100 mmHg" : "Systolic BP ≤ 100 mmHg",
  },
];

// Full SOFA organ system options — index equals score.
const SOFA_SYSTEMS = (isID) => [
  {
    key: "resp",
    title: isID ? "Respirasi" : "Respiration",
    hint: "PaO₂/FiO₂ (mmHg)",
    options: [
      { label: "≥ 400", score: 0 },
      { label: "< 400", score: 1 },
      { label: "< 300", score: 2 },
      { label: isID ? "< 200 & bantuan napas" : "< 200 & vent support", score: 3 },
      { label: isID ? "< 100 & bantuan napas" : "< 100 & vent support", score: 4 },
    ],
  },
  {
    key: "coag",
    title: isID ? "Koagulasi" : "Coagulation",
    hint: isID ? "Trombosit (×10³/µL)" : "Platelets (×10³/µL)",
    options: [
      { label: "≥ 150", score: 0 },
      { label: "< 150", score: 1 },
      { label: "< 100", score: 2 },
      { label: "< 50", score: 3 },
      { label: "< 20", score: 4 },
    ],
  },
  {
    key: "liver",
    title: isID ? "Hepar" : "Hepatic",
    hint: isID ? "Bilirubin (mg/dL)" : "Bilirubin (mg/dL)",
    options: [
      { label: "< 1.2", score: 0 },
      { label: "1.2 – 1.9", score: 1 },
      { label: "2.0 – 5.9", score: 2 },
      { label: "6.0 – 11.9", score: 3 },
      { label: "≥ 12.0", score: 4 },
    ],
  },
  {
    key: "cv",
    title: isID ? "Kardiovaskular" : "Cardiovascular",
    hint: isID ? "MAP / vasopresor (mcg/kg/menit)" : "MAP / vasopressor (mcg/kg/min)",
    options: [
      { label: "MAP ≥ 70", score: 0 },
      { label: "MAP < 70", score: 1 },
      { label: isID ? "Dopamin ≤ 5 atau dobutamin (dosis apa saja)" : "Dopamine ≤ 5 or dobutamine (any dose)", score: 2 },
      { label: isID ? "Dopamin > 5 atau NE / Epi ≤ 0.1" : "Dopamine > 5 or NE / Epi ≤ 0.1", score: 3 },
      { label: isID ? "Dopamin > 15 atau NE / Epi > 0.1" : "Dopamine > 15 or NE / Epi > 0.1", score: 4 },
    ],
  },
  {
    key: "cns",
    title: isID ? "Sistem saraf pusat" : "CNS",
    hint: "Glasgow Coma Scale (GCS)",
    options: [
      { label: "15", score: 0 },
      { label: "13 – 14", score: 1 },
      { label: "10 – 12", score: 2 },
      { label: "6 – 9", score: 3 },
      { label: "< 6", score: 4 },
    ],
  },
  {
    key: "renal",
    title: isID ? "Ginjal" : "Renal",
    hint: isID ? "Kreatinin (mg/dL) / UO (mL/hari)" : "Creatinine (mg/dL) / UO (mL/day)",
    options: [
      { label: "< 1.2", score: 0 },
      { label: "1.2 – 1.9", score: 1 },
      { label: "2.0 – 3.4", score: 2 },
      { label: isID ? "3.5 – 4.9 atau UO < 500" : "3.5 – 4.9 or UO < 500", score: 3 },
      { label: isID ? "≥ 5.0 atau UO < 200" : "≥ 5.0 or UO < 200", score: 4 },
    ],
  },
];

function Toggle({ active, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={active}
      onClick={() => onChange(!active)}
      data-testid={testid}
      className={cn(
        "relative inline-flex h-8 w-14 items-center rounded-full border-2 transition-colors shrink-0",
        active ? "bg-red-600 border-red-600" : "bg-stone-200 border-stone-300"
      )}
    >
      <span
        className={cn(
          "inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform",
          active ? "translate-x-6" : "translate-x-0.5"
        )}
      />
    </button>
  );
}

function ScoreBadge({ score }) {
  const color =
    score === 0
      ? "bg-stone-100 text-stone-600 border-stone-300"
      : score === 1
      ? "bg-yellow-100 text-yellow-800 border-yellow-300"
      : score === 2
      ? "bg-orange-200 text-orange-900 border-orange-400"
      : score === 3
      ? "bg-red-200 text-red-900 border-red-400"
      : "bg-red-600 text-white border-red-700";
  return (
    <span className={cn("inline-flex items-center justify-center h-6 w-6 rounded-full border font-mono text-xs font-bold", color)}>
      {score}
    </span>
  );
}

export default function Sofa() {
  const { lang } = useLanguage();
  const isID = lang === "id";
  const [mode, setMode] = useState("qsofa"); // 'qsofa' | 'sofa'

  const [q, setQ] = useState({ mental: false, rr: false, sbp: false });
  const qScore = Object.values(q).filter(Boolean).length;

  const [s, setS] = useState({ resp: 0, coag: 0, liver: 0, cv: 0, cns: 0, renal: 0 });
  const sTotal = useMemo(() => Object.values(s).reduce((a, b) => a + b, 0), [s]);

  const qItems = QSOFA_ITEMS(isID);
  const systems = SOFA_SYSTEMS(isID);

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-10 py-8 md:py-12" data-testid="sofa-page">
      <header className="border-b border-stone-200 pb-5 mb-8">
        <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-sky-700 mb-2 flex items-center gap-2">
          <Activity size={14} strokeWidth={1.5} />
          {isID ? "Skrining Sepsis · Disfungsi Organ" : "Sepsis Screening · Organ Dysfunction"}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-medium text-stone-900 tracking-tight leading-tight">
          {isID ? "SOFA & qSOFA" : "SOFA & qSOFA"}
        </h1>
        <p className="mt-3 text-sm md:text-base text-stone-600 leading-relaxed max-w-2xl">
          {isID
            ? "qSOFA: skrining cepat 3 kriteria di samping tempat tidur. SOFA lengkap: skor disfungsi 6 sistem organ (Sepsis-3)."
            : "qSOFA: 3-criteria rapid bedside screen. Full SOFA: 6-organ-system dysfunction score (Sepsis-3)."}
        </p>
      </header>

      {/* Mode toggle */}
      <div className="inline-flex border border-stone-300 rounded-md overflow-hidden mb-8" role="tablist" data-testid="sofa-mode-toggle">
        {["qsofa", "sofa"].map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => setMode(m)}
            data-testid={`sofa-mode-${m}`}
            className={cn(
              "px-5 py-2.5 text-sm font-medium transition-colors",
              mode === m ? "bg-sky-700 text-white" : "bg-white text-stone-600 hover:bg-stone-50"
            )}
          >
            {m === "qsofa" ? "qSOFA" : isID ? "SOFA Lengkap" : "Full SOFA"}
          </button>
        ))}
      </div>

      {mode === "qsofa" ? (
        <section data-testid="qsofa-panel">
          <div className="space-y-3">
            {qItems.map((item) => (
              <div
                key={item.key}
                className="flex items-center justify-between border border-stone-200 bg-white rounded-md px-5 py-4"
              >
                <span className="text-sm md:text-base text-stone-900 font-medium pr-4">{item.label}</span>
                <Toggle
                  active={q[item.key]}
                  onChange={(v) => setQ((old) => ({ ...old, [item.key]: v }))}
                  testid={`qsofa-toggle-${item.key}`}
                />
              </div>
            ))}
          </div>

          <div
            data-testid="qsofa-result"
            className={cn(
              "mt-6 border-2 rounded-md p-5 flex items-center gap-4",
              qScore >= 2 ? "border-red-600 bg-red-50" : "border-stone-200 bg-white"
            )}
          >
            <div
              className={cn(
                "h-14 w-14 rounded-full flex items-center justify-center font-display text-2xl font-medium",
                qScore >= 2 ? "bg-red-600 text-white" : "bg-stone-100 text-stone-700"
              )}
            >
              {qScore}
            </div>
            <div className="flex-1">
              <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-stone-500">
                {isID ? "Skor qSOFA" : "qSOFA score"}
              </div>
              <div className="font-display text-lg font-medium text-stone-900">
                {qScore >= 2
                  ? isID
                    ? "Risiko sepsis tinggi"
                    : "High sepsis risk"
                  : isID
                  ? "Risiko rendah"
                  : "Low risk"}
              </div>
              <p className="mt-1 text-sm text-stone-700 leading-relaxed">
                {qScore >= 2
                  ? isID
                    ? "Aktifkan protokol sepsis: kultur darah, laktat, antibiotik spektrum luas dalam 1 jam, resusitasi cairan 30 mL/kg, pertimbangkan vasopresor bila MAP < 65 setelah cairan."
                    : "Activate sepsis protocol: blood cultures, lactate, broad-spectrum antibiotics within 1 h, fluid resuscitation 30 mL/kg, consider vasopressors if MAP < 65 after fluids."
                  : isID
                  ? "Lanjutkan evaluasi klinis. Nilai ulang bila kondisi memburuk."
                  : "Continue clinical evaluation. Reassess if condition deteriorates."}
              </p>
            </div>
            {qScore >= 2 && <ShieldAlert size={28} strokeWidth={1.5} className="text-red-600 shrink-0" />}
          </div>
        </section>
      ) : (
        <section data-testid="sofa-panel">
          <div className="space-y-5">
            {systems.map((sys) => (
              <div key={sys.key} className="border border-stone-200 bg-white rounded-md p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-display text-base font-medium text-stone-900">{sys.title}</div>
                    <div className="text-xs text-stone-500 font-mono mt-0.5">{sys.hint}</div>
                  </div>
                  <ScoreBadge score={s[sys.key]} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-1.5">
                  {sys.options.map((opt) => {
                    const active = s[sys.key] === opt.score;
                    return (
                      <button
                        key={opt.score}
                        type="button"
                        data-testid={`sofa-${sys.key}-${opt.score}`}
                        onClick={() => setS((old) => ({ ...old, [sys.key]: opt.score }))}
                        className={cn(
                          "text-left text-xs md:text-[13px] leading-tight border rounded px-3 py-2.5 min-h-[52px] transition-colors flex flex-col justify-between",
                          active
                            ? "border-sky-700 bg-sky-700 text-white shadow-sm"
                            : "border-stone-200 bg-stone-50 text-stone-700 hover:border-sky-700/50 hover:bg-white"
                        )}
                      >
                        <span className="text-[10px] font-mono opacity-80">+{opt.score}</span>
                        <span className="font-medium">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div
            data-testid="sofa-result"
            className={cn(
              "mt-6 sticky bottom-4 border-2 rounded-md p-5 flex items-center gap-4 shadow-lg",
              sTotal >= 10
                ? "border-red-600 bg-red-50"
                : sTotal >= 5
                ? "border-orange-600 bg-orange-50"
                : "border-stone-200 bg-white"
            )}
          >
            <div
              className={cn(
                "h-14 w-14 rounded-full flex items-center justify-center font-display text-2xl font-medium",
                sTotal >= 10
                  ? "bg-red-600 text-white"
                  : sTotal >= 5
                  ? "bg-orange-600 text-white"
                  : "bg-stone-100 text-stone-700"
              )}
            >
              {sTotal}
            </div>
            <div className="flex-1">
              <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-stone-500">
                {isID ? "Total skor SOFA" : "Total SOFA score"} <span className="text-stone-400">/ 24</span>
              </div>
              <div className="font-display text-lg font-medium text-stone-900">
                {sTotal === 0
                  ? isID
                    ? "Tidak ada disfungsi organ"
                    : "No organ dysfunction"
                  : sTotal <= 4
                  ? isID
                    ? "Disfungsi organ ringan"
                    : "Mild organ dysfunction"
                  : sTotal <= 9
                  ? isID
                    ? "Disfungsi organ sedang"
                    : "Moderate organ dysfunction"
                  : isID
                  ? "Disfungsi organ berat — mortalitas > 40%"
                  : "Severe organ dysfunction — mortality > 40%"}
              </div>
              <p className="mt-1 text-xs text-stone-600 leading-relaxed">
                {isID
                  ? "Kenaikan ≥ 2 poin akut pada pasien terduga infeksi = definisi sepsis (Sepsis-3)."
                  : "Acute rise ≥ 2 points in suspected infection = sepsis definition (Sepsis-3)."}
              </p>
            </div>
            {sTotal >= 5 && <AlertTriangle size={28} strokeWidth={1.5} className={sTotal >= 10 ? "text-red-600" : "text-orange-600"} />}
          </div>
        </section>
      )}

      <div className="mt-10 max-w-2xl">
        <div className="border-l-2 border-amber-700/60 pl-4 py-1 text-xs text-stone-600 leading-relaxed">
          <span className="font-semibold text-stone-900">{isID ? "Sebagai rujukan saja." : "Reference only."}</span>{" "}
          {isID
            ? "qSOFA adalah alat skrining, bukan diagnosis. SOFA membutuhkan data laboratorium — laporan dini ke tim bila kriteria terpenuhi."
            : "qSOFA is a screening tool, not a diagnosis. SOFA requires laboratory data — escalate early to your team if criteria are met."}
        </div>
      </div>
    </div>
  );
}
